import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { useAuth } from '../auth/auth-store';
import { useRealtimeTopic } from '../app/realtime-store';
import { setMyAvailability, teamAvailability, type Availability } from './api';

/**
 * Whether you are taking conversations (context.md §45–§56). Phase 74.
 *
 * The control is small and the thing behind it is not: until now every member of a queue was
 * treated as available, so a conversation arriving in the evening was assigned to whoever the
 * rotation landed on and sat unread until morning with its clock running.
 *
 * Three states, because two are not enough for how people actually work. `AVAILABLE` takes new
 * conversations. `AWAY` is not here. **`BUSY` is the one that earns its place**: somebody deep in a
 * difficult case is working — they should keep what they have and not be handed anything new — and
 * without it they either lie and say away, or take work they cannot start.
 *
 * It is your own status and nobody else's. An administrator switching a colleague to available is
 * an administrator putting a customer in front of an empty chair; removing them from the queue is
 * the honest tool, and it is audited.
 */

const OPTIONS: { value: Availability; tone: string }[] = [
  { value: 'AVAILABLE', tone: 'is-available' },
  { value: 'BUSY', tone: 'is-busy' },
  { value: 'AWAY', tone: 'is-away' },
];

export function AvailabilityPicker(): React.JSX.Element | null {
  const client = useQueryClient();
  const { user } = useAuth();

  const team = useQuery({
    queryKey: ['inbox-availability'],
    queryFn: teamAvailability,
    // The socket keeps this current; the interval is the floor under it.
    refetchInterval: 5 * 60_000,
  });

  // Somebody else changing theirs moves this list without a refresh.
  useRealtimeTopic('presence', () => {
    void client.invalidateQueries({ queryKey: ['inbox-availability'] });
  });

  const change = useMutation({
    mutationFn: (next: Availability) => setMyAvailability(next),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['inbox-availability'] }),
  });

  const members = team.data?.members ?? [];
  const mine = members.find((member) => member.userId === user?.id);

  if (!user) return null;

  const current = mine?.availability ?? 'AVAILABLE';

  /*
   * How many colleagues are ready to take something.
   *
   * Both facts, because either alone misleads: three people marked available who all closed the tab
   * is not three people, and one person here who marked themselves away is not somebody to route to.
   */
  const ready = members.filter(
    (member) => member.availability === 'AVAILABLE' && member.connected,
  ).length;

  return (
    <div className="availability">
      <label className="availability__mine">
        <span
          className={`availability__dot ${OPTIONS.find((option) => option.value === current)?.tone ?? ''}`}
          aria-hidden="true"
        />
        <select
          className="select select--bare"
          value={current}
          disabled={change.isPending}
          aria-label={t('inbox.availability')}
          onChange={(event) => change.mutate(event.target.value as Availability)}
        >
          {OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(`inbox.availability.${option.value}`)}
            </option>
          ))}
        </select>
      </label>

      {/* Only where there is a team; on your own it is a number about yourself. */}
      {members.length > 1 && (
        <span className="muted small" title={t('inbox.readyHint')}>
          {t('inbox.ready').replace('{count}', String(ready))}
        </span>
      )}
    </div>
  );
}
